import { useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Sparkles, Keyboard } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { uploadChunks } from "../kit/PdfImport";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { RequestFields, BLANK_REQUEST } from "./RequestFields";
import { Pills } from "./BuildDialog";

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export const UploadBespoke = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("pick");
  const [pct, setPct] = useState(0);
  const [iid, setIid] = useState(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState(BLANK_REQUEST);
  const [k, setK] = useState({ collection: "researched", edition: "luxury", tier: "premium", price: "" });
  const reset = () => { setStep("pick"); setPct(0); setIid(null); setFile(null); setF(BLANK_REQUEST); setK({ collection: "researched", edition: "luxury", tier: "premium", price: "" }); };
  const upload = async (file) => {
    setFile(file); setStep("uploading");
    try { setIid(await uploadChunks(file, { bulk: true }, setPct)); setStep("how"); }
    catch (e) { toast.error(errMsg(e)); setStep("pick"); }
  };
  const manual = () => { setF({ ...BLANK_REQUEST, destination: file.name.replace(/\.(pdf|html?)$/i, "").replace(/[-_]+/g, " "), start_date: addDays(30), end_date: addDays(36) }); setStep("form"); };
  const read = async () => {
    setStep("reading");
    try {
      const { data } = await http.post(`/bespoke/imports/${iid}/read`);
      const c = data.customer;
      const start = c.start_date || addDays(30);
      const end = c.end_date || (() => { const d = new Date(start); d.setDate(d.getDate() + (data.kit.days || 7) - 1); return d.toISOString().slice(0, 10); })();
      setF({ ...BLANK_REQUEST, ...Object.fromEntries(Object.entries(c).filter(([, v]) => v)), start_date: start, end_date: end });
      setK((s) => ({ ...s, edition: data.kit.edition, tier: data.kit.tier }));
      setStep("form");
      if (!c.start_date) toast.message("No trip dates found in the PDF — please set them");
    } catch (e) { toast.error(errMsg(e)); setStep("how"); }
  };
  const create = async (e) => {
    e.preventDefault();
    setStep("saving");
    try {
      const { website, ...rest } = f;
      await http.post(`/bespoke/imports/${iid}/create`, { ...rest, ...k, price: Number(k.price) });
      toast.success("Bespoke kit uploaded — it's being read into the studio now");
      setOpen(false); reset(); onDone();
    } catch (ex) { toast.error(errMsg(ex)); setStep("form"); }
  };
  return (
    <>
      <button data-testid="bespoke-upload-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50"><FileUp size={14} /> Upload bespoke PDF</button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent data-testid="bespoke-upload-dialog" className="bg-[#FAF9F6] text-[#2B2A26] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Upload a bespoke kit</DialogTitle></DialogHeader>
          {step === "pick" && <label data-testid="bespoke-upload-drop" className="block border-2 border-dashed border-stone-300 rounded-lg p-10 text-center cursor-pointer hover:bg-white">
            <FileUp className="mx-auto text-stone-400 mb-2" /><div className="font-medium">Choose a bespoke kit PDF</div><div className="text-sm text-stone-500">It's read into the studio like your other imported kits</div>
            <input data-testid="bespoke-upload-input" type="file" accept=".pdf,.html,.htm" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>}
          {step === "uploading" && <div data-testid="bespoke-upload-progress" className="py-8 text-center text-sm text-stone-600"><Loader2 className="mx-auto animate-spin mb-2" />Uploading {file?.name} · {pct}%</div>}
          {step === "how" && <div className="grid sm:grid-cols-2 gap-3">
            <button data-testid="bespoke-upload-read" onClick={read} className="rounded-lg border border-stone-200 bg-white p-5 text-left hover:border-[#C9A847]"><Sparkles size={18} className="text-[#C9A847] mb-2" /><div className="font-medium">Read the details for me</div><div className="text-sm text-stone-500">Name, destination, dates and edition from the PDF — you check them</div></button>
            <button data-testid="bespoke-upload-manual" onClick={manual} className="rounded-lg border border-stone-200 bg-white p-5 text-left hover:border-[#C9A847]"><Keyboard size={18} className="text-stone-500 mb-2" /><div className="font-medium">I'll type them myself</div><div className="text-sm text-stone-500">No AI used for the details</div></button>
          </div>}
          {step === "reading" && <div className="py-8 text-center text-sm text-stone-600"><Loader2 className="mx-auto animate-spin mb-2" />Reading the PDF…</div>}
          {(step === "form" || step === "saving") && <form onSubmit={create} className="space-y-6">
            <RequestFields value={f} onChange={setF} emailOptional />
            <div className="grid sm:grid-cols-2 gap-4">
              {["collection", "edition", "tier"].map((x) => <div key={x}><div className="text-xs uppercase tracking-[0.14em] text-stone-500 mb-1.5">{{ collection: "Layout", edition: "Edition", tier: "Depth" }[x]}</div><Pills name={x} value={k[x]} onPick={(v) => setK((s) => ({ ...s, [x]: v }))} /></div>)}
              <div><div className="text-xs uppercase tracking-[0.14em] text-stone-500 mb-1.5">Price charged (AUD) *</div>
                <Input data-testid="bespoke-upload-price" type="number" min={1} required value={k.price} onChange={(e) => setK((s) => ({ ...s, price: e.target.value }))} className="h-11 w-48 bg-white" /></div>
            </div>
            <button data-testid="bespoke-upload-save" disabled={step === "saving"} className="rounded-sm bg-[#2B2A26] text-white px-6 py-3 font-bold disabled:opacity-50">{step === "saving" ? "Saving…" : "Save bespoke kit"}</button>
          </form>}
        </DialogContent>
      </Dialog>
    </>
  );
};
